/**
 * Mobile in-card totals + global Update Cart button
 *
 * Two responsibilities, both gated to the mobile breakpoint:
 *   1. Single global Update Cart button (rendered above .mobile-inventory-list
 *      in main-product.liquid) — submits all dirty inputs through the same
 *      performCartUpdate path desktop's per-row buttons use. Disabled when
 *      nothing is dirty; label is always just "Update Cart" (no qty/$ shown
 *      since the cart spans every visible card).
 *
 *   2. Per-card totals inside each .mobile-inventory-table:
 *        - .mit-row-total : price * sum(qty) for that row
 *        - .mit-wh-total  : sum(qty * price) for that warehouse column
 *        - .mit-grand-total : sum across all rows + warehouses
 *      Refreshed on any input/change event from canonical or mobile mirror
 *      inputs. Reads canonical inputs as the source of truth.
 */

import { performCartUpdate, showCartUpdateErrorPopup } from "../handlers/addToCart.js";
import { isMobileViewport } from "../utils/viewport.js";
import { isAnyDirty } from "./dirtyTracking.js";
import { validateGridQuantities } from "../utils/validation.js";
import { showCustomPopup } from "./popup.js";
import { formatNumber } from "../utils/formatters.js";

function hasPricingGridInputs(): boolean {
  return document.querySelector(".variant_qty_input_directship") !== null;
}

/**
 * Look up the canonical input behind a mobile mirror cell. Returns null when
 * the canonical isn't in the DOM yet (e.g. lazy-loaded card before its row).
 */
function getCanonicalForMirror(
  mirror: HTMLInputElement,
): HTMLInputElement | null {
  const sourceId = mirror.dataset.sourceInputId;
  if (!sourceId) return null;
  const source = document.getElementById(sourceId);
  return source instanceof HTMLInputElement ? source : null;
}

/**
 * Refresh the global Update Cart button: enabled iff any input is dirty.
 * Label is static — total qty/dollars are shown in the per-card totals row
 * instead, where the user is looking when typing.
 */
function refreshGlobalSubmit(): void {
  const $buttons = $(".mobile_table_wrapper > .mobile-card-submit");
  if ($buttons.length === 0) return;

  if (!isMobileViewport() || !hasPricingGridInputs()) {
    $buttons.prop("disabled", true).addClass("is-clean");
    return;
  }

  const dirty = isAnyDirty();
  $buttons.prop("disabled", !dirty).toggleClass("is-clean", !dirty);
}

/**
 * Recompute every total in every visible card. Totals are item *counts*, not
 * dollar amounts — buyers think in units when filling a wholesale grid:
 *   - per-row total = sum of qty across this row's warehouses
 *   - per-warehouse-column total = sum of qty across this column
 *   - grand total = sum of all entered qty in the card
 * Reads canonical input values via the data-source-input-id pointer on each
 * mirror cell — same source of truth dirty tracking and cart submission use.
 */
function refreshAllCardTotals(): void {
  const cards = document.querySelectorAll<HTMLElement>(
    ".mobile-inventory-product",
  );
  cards.forEach((card) => refreshCardTotals(card));
}

function refreshCardTotals(card: HTMLElement): void {
  const warehouseTotals = new Map<string, number>();
  let grandTotal = 0;

  const rows = card.querySelectorAll<HTMLElement>("tbody tr.mit-row");
  rows.forEach((row) => {
    let rowTotal = 0;

    row.querySelectorAll<HTMLElement>(".mit-qty-cell").forEach((cell) => {
      const mirror = cell.querySelector<HTMLInputElement>(
        ".mobile-size-cell__qty",
      );
      if (!mirror) return; // unavailable placeholder cell

      const source = getCanonicalForMirror(mirror);
      if (!source) return;

      const qty = parseInt(source.value, 10) || 0;
      if (qty <= 0) return;

      rowTotal += qty;

      const whCode = cell.dataset.warehouseCode;
      if (whCode) {
        warehouseTotals.set(
          whCode,
          (warehouseTotals.get(whCode) ?? 0) + qty,
        );
      }
    });

    const rowTotalNode = row.querySelector<HTMLElement>(".mit-row-total");
    // Right-side sums render blank at zero so an empty card doesn't look
    // peppered with "0" labels. Bottom-row per-warehouse totals keep the
    // explicit 0 so the totals row stays visually intact.
    if (rowTotalNode) {
      rowTotalNode.textContent = rowTotal > 0 ? formatNumber(rowTotal) : "";
    }
    grandTotal += rowTotal;
  });

  card
    .querySelectorAll<HTMLElement>(".mit-totals-row .mit-wh-total")
    .forEach((node) => {
      const whCode = node.dataset.warehouseCode ?? "";
      node.textContent = formatNumber(warehouseTotals.get(whCode) ?? 0);
    });

  const grandNode = card.querySelector<HTMLElement>(".mit-grand-total");
  if (grandNode) {
    grandNode.textContent = grandTotal > 0 ? formatNumber(grandTotal) : "";
  }
}

function refreshAll(): void {
  refreshGlobalSubmit();
  refreshAllCardTotals();
}

/**
 * Initialize the global Update Cart button + totals refresh hooks.
 *
 * Idempotent in spirit but called once from index.ts after init. Returns
 * early if no button is on the page (defensive — non-product pages loading
 * wp-custom.js wouldn't have it).
 */
export function initMobileCardSubmit(): void {
  // Refresh on any input/change in the grid (canonical or mobile mirror).
  $(document).on(
    "input change",
    ".variant_qty_input_directship, .mobile-size-cell__qty",
    refreshAll,
  );
  // Window resize crosses the mobile breakpoint (rotation, devtools).
  window.addEventListener("resize", refreshAll);

  // Click → submit (same path as desktop's per-row Update Cart).
  $(document).on(
    "click",
    ".mobile_table_wrapper > .mobile-card-submit",
    async function () {
      const button = this as HTMLButtonElement;
      if (button.disabled) return;

      // Validate before disabling so the user can correct and retry without
      // being held hostage by an in-flight request.
      const validation = validateGridQuantities();
      if (!validation.valid) {
        showCustomPopup(
          "Please correct the following errors:<br>" +
            validation.errors.join("<br>"),
          0,
        );
        return;
      }

      button.disabled = true;
      button.classList.add("loading");
      try {
        await performCartUpdate({ showSuccessPopup: true });
      } catch (error) {
        console.error("Mobile Update Cart failed:", error);
        showCartUpdateErrorPopup();
      } finally {
        button.disabled = false;
        button.classList.remove("loading");
        // Baselines were just reset by performCartUpdate → button should
        // re-disable. Totals stay populated since canonical values persist.
        refreshAll();
      }
    },
  );

  refreshAll();
}

/**
 * Force a refresh of the button + totals. Called after cart pre-population
 * and after baseline resets so the UI reflects the new dirty state.
 */
export function refreshMobileCardSubmit(): void {
  refreshAll();
}
